import {
  reactive,
  ref,
  useStore,
  useContext,
  watch,
  computed,
  onMounted,
} from '@nuxtjs/composition-api';

import useForm from '~/composables/useForm';
import useFetchCategory from '~/composables/useFetchCategory';
import UiAutocomplete from '~/components/ui/UiAutocomplete.vue';
import UiInput from '~/components/ui/UiInput.vue';
import UiSelect from '~/components/ui/UiSelect.vue';
import UiCheckbox from '~/components/ui/UiCheckbox.vue';
import ServiceFormProvider from '~/components/ServiceFormProvider.vue';
import { extend } from 'vee-validate';
import { useRecaptcha } from '~/composables';


export default {
  __name: 'ServiceForm',
  emits: ['success-data-send'],
  setup(__props, { emit }) {

const getFullAddressStringFromAddressObj = (addressObj) => {
  return `${addressObj.city}, ${addressObj.postcode}, ${addressObj.street} ${addressObj.house_number}`;
};

extend('suggestionsIncludes', {
  validate(value, args) {
    const { suggestions } = args;
    const preparedValue = value.toLocaleLowerCase();
    return suggestions.some((item) =>
      getFullAddressStringFromAddressObj(item)
        .toLocaleLowerCase()
        .includes(preparedValue),
    );
  },
  message(_, args) {
    return args.errorMsg;
  },
  params: ['suggestions', 'errorMsg'],
});
extend('isFullAddress', {
  validate(value, args) {
    const { selectedAddress } = args;
    return selectedAddress.toLocaleLowerCase() === value.toLocaleLowerCase();
  },
  message(_, args) {
    return args.errorMsg;
  },
  params: ['selectedAddress', 'errorMsg'],
});


const { submit, formPending } = useForm();
const { nuxtState, $recaptcha } = useContext();
const store = useStore();
const { fetchCategories, allCategories: categoriesList } = useFetchCategory();
const { validateRecaptcha } = useRecaptcha();

const config = store.getters['config/getConfig'];
const lang = store.getters['config/getActiveLanguage'];

const form = ref();
const searchedAddress = ref('');
const autocompleteRef = ref();
const postalCodeValidationProvider = ref();
const countryValidationProvider = ref();
const autocompleteValidationProvider = ref();
const serviceFormProviderRef = ref();

const isMounted = ref(false);
const recaptchaClicked = ref(false);

const DEFAULT_FORM_DATA = {
  site: config.domain, // не используется
  lang, // не используется
  name: '',
  email: '',
  phone: '',
  category: undefined,
  sn: '',
  model: undefined,
  purchaseDate: '',
  deliveryAgent: undefined,
  country: undefined,
  buildingNumber: '', // не используется
  city: '',
  apartmentNumber: '', // не используется
  street: '',
  postalCode: '',
  faultDescription: '',
  personalData: false,
  fullAddress: '',
  state: '',
  token: '', // не используется
};
const CLEARABLE_DELIVERY_FIELDS = [
  'country',
  'city',
  'buildingNumber',
  'apartmentNumber',
  'street',
  'postalCode',
  'fullAddress',
  'state',
];
const CLEARABLE_DELIVERY_FIELDS_OMIT_COUNTRY = CLEARABLE_DELIVERY_FIELDS.filter(
  (field) => field !== 'country',
);

const formData = reactive({
  ...DEFAULT_FORM_DATA,
});

const categories = computed(() =>
  categoriesList.value.map((categoryItem) => ({
    id: categoryItem.slug,
    name: categoryItem.name,
  })),
);

const clearForm = (formFields) => {
  if (formFields?.length) {
    formFields.forEach(
      (fieldName) => (formData[fieldName] = DEFAULT_FORM_DATA[fieldName]),
    );
  } else {
    Object.assign(formData, DEFAULT_FORM_DATA);
  }
};
const resetValidationObserver = () => {
  form.value?.reset();
};

const isFormFieldAvailable = (fieldName) => {
  const availableFormFields =
    serviceFormProviderRef.value.getAvailableFormFields();
  return availableFormFields.includes(fieldName);
};

const createFormPayload = (formState) => {
  return {
    name: formState.name,
    phone: formState.phone,
    email: formState.email,
    delivery_agent: formState.deliveryAgent,
    country: serviceFormProviderRef.value?.countriesListForService.find(
      (country) => country.id === formState.country,
    ).name,
    zipcode_or_street: formState.fullAddress,
    product_type: formState.category,
    device: formState.model,
    serial_number: formState.sn,
    purchase_date: formState.purchaseDate,
    message: formState.faultDescription,
    agreement: formState.personalData,
  };
};
const submitForm = async () => {
  const isValidRecapcha = await validateRecaptcha();
  if (!isValidRecapcha) return;

  const formId = nuxtState.config.serviceFormUuid;
  const resultFormData = {
    ...createFormPayload(formData),
    page_url: window.location.href,
  };

  await submit(resultFormData, formId);
  emit('success-data-send');
  clearForm();
  resetValidationObserver();
};

const handleSelectCountry = (countryName) => {
  // Тут выполняется поиск подсказок для DPD по выбранной стране,
  // поиск подсказок для UPS вынесен в ServiceFormProvider -> watchDebounced
  // и происходит каждый раз
  // при изменении одного из полей formData: city, state, country, postalCode, street
  // с задержкой в 1500мс, перед этим происходит получение токена для ups в watch сразу
  // после изменения поля formData.deliveryAgent
  if (formData.deliveryAgent === 'dpd' && countryName) {
    serviceFormProviderRef.value.dpd.getDpdCitiesBySelectedCountry(
      serviceFormProviderRef.value?.countriesListForService.find(
        (country) => country.id === countryName,
      ).name,
    );
  }

  clearForm(CLEARABLE_DELIVERY_FIELDS_OMIT_COUNTRY);
  // очищается v-model autocomplete (searchedAddress),
  searchedAddress.value = '';
};

const searchAddress = (event) => {
  searchedAddress.value = event.query;
  if (formData.deliveryAgent !== 'dpd') return;

  if (!searchedAddress.value) {
    serviceFormProviderRef.value.setSuggestions();
    return;
  }
  const dpdLocationsFromProvider =
    serviceFormProviderRef.value.getLocations('dpd');
  const suggestions = dpdLocationsFromProvider.filter((dpdAddress) => {
    const fullAddress = getFullAddressStringFromAddressObj(dpdAddress);
    return fullAddress
      .toLowerCase()
      .includes(searchedAddress.value.toLowerCase().trim());
  });
  serviceFormProviderRef.value.setSuggestions(suggestions);
};

const handleSelectAddress = (event) => {
  const fullAddress = getFullAddressStringFromAddressObj(event.value);
  searchedAddress.value = fullAddress;
  formData.fullAddress = fullAddress;
  if (formData.deliveryAgent === 'dpd') {
    formData.city = event.value.city;
    formData.postalCode = event.value.postalCode;
  }
};

const isSubmitDisabled = (invalid) => {
  return invalid || !recaptchaClicked.value || formPending.value;
};
const handleFocusAutocomplete = () => {
  if (formData.deliveryAgent === 'ups')
    autocompleteRef.value.$refs.autocomplete.showOverlay();
};
const handleUpsLocationWasSearched = () => {
  searchedAddress.value = '';
  formData.fullAddress = '';
};

const resetFullAddressRelatedFieldsErrors = () => {
  autocompleteValidationProvider.value.reset();
  postalCodeValidationProvider.value?.reset();
  countryValidationProvider.value.reset();
};

watch(
  () => formData.deliveryAgent,
  () => {
    clearForm(CLEARABLE_DELIVERY_FIELDS);
    resetFullAddressRelatedFieldsErrors();
    searchedAddress.value = '';
  },
);

onMounted(async () => {
  isMounted.value = true;
  await $recaptcha.init();
  fetchCategories();
});

return { __sfc: true,getFullAddressStringFromAddressObj, emit, submit, formPending, nuxtState, $recaptcha, store, fetchCategories, categoriesList, validateRecaptcha, config, lang, form, searchedAddress, autocompleteRef, postalCodeValidationProvider, countryValidationProvider, autocompleteValidationProvider, serviceFormProviderRef, isMounted, recaptchaClicked, DEFAULT_FORM_DATA, CLEARABLE_DELIVERY_FIELDS, CLEARABLE_DELIVERY_FIELDS_OMIT_COUNTRY, formData, categories, clearForm, resetValidationObserver, isFormFieldAvailable, createFormPayload, submitForm, handleSelectCountry, searchAddress, handleSelectAddress, isSubmitDisabled, handleFocusAutocomplete, handleUpsLocationWasSearched, resetFullAddressRelatedFieldsErrors, UiAutocomplete, UiInput, UiSelect, UiCheckbox, ServiceFormProvider }
}

}